import { ImpSpinner } from '@imprint-payments/imprint-ui';
import { useEffect, useRef, useState } from 'react';

import { EVENT } from '../../analytics/analyticsConsts';
import { callPrompt, consumerLogOut } from '../../api/api';
import { useVerifyOtp } from '../../api/verifyOtp.api';
import { resetDeviceID } from '../../app/apiScreenSlice';
import {
  MODAL_ENUM,
  setLoading,
  setShowModal,
} from '../../app/applicationScreenSlice';
import { resetConsumerInfoSlice } from '../../app/consumerInfoSlice';
import { useAppSelector, useImpState } from '../../app/hooks';
import { setLoginFlow, setMultiCardUser } from '../../app/loginSlice';
import { setIncorrectOtpCode } from '../../app/otpScreenSlice';
import { SCREEN, setScreen } from '../../app/screenStateSlice';
import close from '../../assets/icons/close.svg';
import imprintLogo from '../../assets/icons/logo-imprint.svg';
import downloadAppQRCodeGeneric from '../../assets/images/downloadAppQRCode_generic.svg';
import { ANALYTICS } from '../../firebase/firebase';
import useNextPage from '../../hooks/nextPage';
import { formatDateValue, formatDob } from '../../utils/formatUtils';
import { clearServiceLoginState, redirectTo } from '../../utils/functionUtils';
import {
  validateBirthDate,
  validatePhoneNumber,
  ValidationResponse,
} from '../../utils/validateUtils';
import ButtonServicing, {
  ButtonColorScheme,
} from '../buttonServicing/ButtonServicing';
import DynamicQRCode from '../dynamicQRCode/dynamicQRCode';
import OtpCardNew from '../otp/OtpCardNew';
import PINEntry from '../pinEntry/PINEntry';
import styles from './LoginModal.module.css';

//TODO: Deprecated. Don't use any modals in web-application.
export default function LoginModal({ onClose }: { onClose?: () => void }) {
  const nextPage = useNextPage();

  const {
    language,
    merchantData,
    screenState,
    consumerInfo,
    consumerProfile,
    loginState,
    dispatch,
  } = useImpState();
  const otpScreenState = useAppSelector((state) => state.otpScreenSelector);
  const applicationScreenState = useAppSelector(
    (state) => state.applicationScreenSelector
  );

  const [phoneNumber] = useState('');
  const [birthDayDay, setBirthDayDay] = useState(
    consumerProfile.dob.day === 0 ? '' : String(consumerProfile.dob.day)
  );
  const [birthDayMonth, setBirthDayMonth] = useState(
    consumerProfile.dob.month === 0 ? '' : String(consumerProfile.dob.month)
  );
  const [birthDayYear, setBirthDayYear] = useState(
    consumerProfile.dob.year === 0 ? '' : String(consumerProfile.dob.year)
  );
  const birthDay = formatDob(
    `${formatDateValue(Number(birthDayMonth))}/
    ${formatDateValue(Number(birthDayDay))}/
    ${formatDateValue(Number(birthDayYear))}`
  );
  const [_disabledBtn, setDisabledBtn] = useState(false);
  const [failedOtp, setFailedOtp] = useState(false);
  useAppSelector((state) => state.validatorSelector);
  const outerElementRef = useRef(null);

  useEffect(() => {
    dispatch(setMultiCardUser(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loginState.loginFlow.dobError) {
      setBirthDayDay('');
      setBirthDayMonth('');
      setBirthDayYear('');
    }
  }, [loginState.loginFlow.dobError]);

  useEffect(() => {
    if (screenState.screen === SCREEN.LOGIN) {
      ANALYTICS.logEvent(EVENT.ENTER_PHONE_NUMBER_VIEWED);
    } else if (screenState.screen === SCREEN.DOB) {
      ANALYTICS.logEvent(EVENT.VERIFY_DOB_VIEWED);
    } else if (screenState.screen === SCREEN.PIN) {
      ANALYTICS.logEvent(EVENT.ENTER_PIN_VIEWED);
    } else if (screenState.screen === SCREEN.SERVICE_OTPSCREEN) {
      ANALYTICS.logEvent(EVENT.PHONE_NUMBER_RECOGNIZED_OTP_PAGE_VIEWED, {
        application_product_uuid: merchantData.productUUID,
      });
    } else if (screenState.screen === SCREEN.VERIFICATION_FAILED) {
      ANALYTICS.logEvent(EVENT.LOGIN_FAILED_VIEWED);
    } else if (screenState.screen === SCREEN.ACCOUNT_PENDING) {
      ANALYTICS.logEvent(EVENT.ISSUE_WITH_APPLICATION_PG_VIEWED, {
        application_product_uuid: merchantData.productUUID,
        context: 'user_recognized',
        consumer_uuid: consumerInfo.consumerUUID,
      });
    } else if (screenState.screen === SCREEN.INCOME_HOUSING_MODAL) {
      ANALYTICS.logEvent(EVENT.MULTI_CREDIT_APP_INCOME_HOUSING_PAGE_VIEWED, {
        application_product_uuid: merchantData.productUUID,
        context: 'user_recognized',
        consumer_uuid: consumerInfo.consumerUUID,
      });
    }
    nextPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenState.screen]);

  useEffect(() => {
    if (loginState.loginFlow.dobError) {
      ANALYTICS.logEvent(EVENT.VERIFY_DOB_TRY_AGAIN_VIEWED);
    }
  }, [loginState.loginFlow.dobError]);

  useEffect(() => {
    setDisabledBtn(true);
    if (
      failedOtp ||
      (validatePhoneNumber(phoneNumber) === ValidationResponse.valid &&
        phoneNumber !== '')
    ) {
      setDisabledBtn(false);
    }
  }, [phoneNumber, failedOtp]);

  useEffect(() => {
    setDisabledBtn(true);
    if (
      screenState.screen == SCREEN.DOB &&
      validateBirthDate(birthDay) === ValidationResponse.valid &&
      birthDay !== ''
    ) {
      setDisabledBtn(false);
    }
  }, [birthDay, screenState.screen]);

  useEffect(() => {
    if (otpScreenState.reachedMaxRetries) {
      setFailedOtp(true);
    } else {
      setFailedOtp(false);
    }
  }, [otpScreenState.reachedMaxRetries]);

  useEffect(() => {
    if (loginState.loginFlow.loginError) {
      ANALYTICS.logEvent(EVENT.ENTER_PHONE_NUMBER_NOT_RECOGNIZED_VIEWED);
      dispatch(setLoginFlow({ ...loginState.loginFlow, loginError: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber]);

  useEffect(() => {
    if (loginState.loginFlow.dobError) {
      ANALYTICS.logEvent(EVENT.VERIFY_DOB_TRY_AGAIN_CONTINUE_CLICKED);
    }

    if (birthDay !== '') {
      dispatch(setLoginFlow({ ...loginState.loginFlow, dobError: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [birthDay]);

  const verifyOtpMutation = useVerifyOtp();
  const submitOtpHandler = (code: string) => {
    dispatch(setIncorrectOtpCode(false));
    verifyOtpMutation.mutate(
      { code },
      {
        onSuccess: (data) => {
          dispatch(setIncorrectOtpCode(false));
          dispatch(setLoading(false));

          // TODO: make nextStep as a enum
          if (data.nextStep === 'verifyDOB') {
            dispatch(setScreen(SCREEN.DOB));
          } else if (data.nextStep === 'verifyPII') {
            redirectTo('account', false, false, true);
          } else if (data.nextStep === 'verifyPIN') {
            dispatch(setScreen(SCREEN.PIN));
          } else if (data.nextStep === 'showSignUp') {
            dispatch(callPrompt());
          }
        },
        onError: (err) => {
          if (err.code === '101') {
            dispatch(setIncorrectOtpCode(true));
            dispatch(setLoading(false));
          } else if (err.code === '201') {
            dispatch(setScreen(SCREEN.VERIFICATION_FAILED));
            dispatch(setLoading(false));
          }
        },
      }
    );
    return;
  };

  const renderFailedFlowPage = () => {
    let body = language.maxRetriesBody;

    if (failedOtp) {
      body = language.loginOtpFailedBody;
    }

    return (
      <div className={styles.failedFlowContainer}>
        <h1 className={`${styles.header} ${styles.failedHeader}`}>
          {language.loginOtpFailedHeader}
        </h1>
        <p className={styles.failedSubline}>{body}</p>
      </div>
    );
  };

  const renderAccountPendingPage = () => {
    return (
      <div className={styles.continueApplicationContainer}>
        <div className={styles.qrContainer}>
          <DynamicQRCode
            height="152"
            width="152"
            image={downloadAppQRCodeGeneric || ''}
          />
        </div>
        <h1
          className={`${styles.continueApplicationHeader} ${styles.failedHeader}`}
        >
          {language.accountPendingHeader}
          <br />
          {language.accountPendingHeader2.replace(
            '$programName$',
            merchantData.rewardDetails.cardName
          )}
        </h1>
        <p className={styles.continueApplicationBody}>
          {language.accountPendingBody}
        </p>
        <div className={styles.downloadAppButton}>
          <ButtonServicing
            value={language.downloadBtn}
            handleClick={() => {
              ANALYTICS.logEvent(
                EVENT.ISSUE_WITH_APPLICATION_DOWNLOAD_CLICKED,
                {
                  application_product_uuid: merchantData.productUUID,
                  context: 'user_recognized',
                  consumer_uuid: consumerInfo.consumerUUID,
                }
              );
              window.open(merchantData.deeplink);
            }}
            colorScheme={ButtonColorScheme.ghostBranded}
          />
        </div>
        <ButtonServicing
          value={language.goBackBtn}
          handleClick={() => {
            ANALYTICS.logEvent(EVENT.ISSUE_WITH_APPLICATION_GO_BACK_CLICKED, {
              application_product_uuid: merchantData.productUUID,
              context: 'user_recognized',
              consumer_uuid: consumerInfo.consumerUUID,
            });
            handleCloseBtn();
          }}
          colorScheme={ButtonColorScheme.fillBranded}
        />
      </div>
    );
  };

  const handleCloseBtn = () => {
    dispatch(setShowModal(MODAL_ENUM.noModal));
    dispatch(clearServiceLoginState);
    dispatch(consumerLogOut);
    dispatch(resetDeviceID());
    dispatch(resetConsumerInfoSlice());
    dispatch(setScreen(SCREEN.APPLICATION));
    dispatch(setMultiCardUser(false));
    onClose?.();
  };

  const GetModalContent = () => {
    switch (screenState.screen) {
      case SCREEN.SERVICE_OTPSCREEN: {
        return (
          <OtpCardNew
            title={language.existingUserSubline}
            showLogoHeader
            onFull={submitOtpHandler}
            handleResend={() => {}}
            bottomSubline={language.enterSecurityCode}
          />
        );
      }
      case SCREEN.PIN: {
        return <PINEntry />;
      }
      case SCREEN.ACCOUNT_PENDING: {
        return renderAccountPendingPage();
      }
    }
  };

  const renderModalContent = () => {
    return (
      <>
        {screenState.screen == SCREEN.DOB && (
          <div className={styles.imprintLogoContainer}>
            <img
              draggable="false"
              alt="imprint logo"
              className={styles.imprintLogo}
              src={imprintLogo}
            />
          </div>
        )}
        {screenState.screen == SCREEN.VERIFICATION_FAILED &&
          renderFailedFlowPage()}
        {GetModalContent()}
      </>
    );
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <button
          type="button"
          className={styles.close}
          onClick={handleCloseBtn}
          aria-label="Close popup modal"
          ref={outerElementRef}
        >
          <img src={close} alt="close" aria-hidden="true" />
        </button>
        <div className={styles.container}>
          {applicationScreenState.loading ? (
            <ImpSpinner />
          ) : (
            renderModalContent()
          )}
        </div>
      </div>
    </div>
  );
}
